export class Utils {

    static formatNumber = (num: any, showCurrency: boolean = true, isFixed: boolean = false): string => {
        if (isNaN(num)) {
            return num;
        }

        return (showCurrency ? '₹ ' : '') + parseFloat(isFixed ? parseFloat(num).toFixed(2) : num).toLocaleString('en-IN');
    }

    static formatNumberWithSuffix = (num:any, showCurrency: boolean = true): string => {
        if (isNaN(num)) {
            return (showCurrency ? '₹ ' : '') + '0';
        }
        if (num >= 10000000) {
            return (showCurrency ? '₹ ' : '') + (num / 10000000).toFixed(2) + ' Cr';
        } else if (num >= 100000) {
            return (showCurrency ? '₹ ' : '') + (num / 100000).toFixed(2) + ' Lakh';
        } else {
            return (showCurrency ? '₹ ' : '') + parseFloat(num).toLocaleString('en-IN');
        }
    }

    /**
     * Compact currency for charts/axes/labels.
     * Cr for >= 1 crore, L for >= 1 lakh, K for >= 1 thousand, else the plain
     * rounded amount. Always rounds so floating-point noise like
     * 184.51999999999998 renders as "185" (or "184.52" when decimals kept).
     *
     * @param num       value to format
     * @param opts.space  put a space after ₹ (default true → "₹ 12K", false → "₹12K")
     * @param opts.decimals  decimals for the small (< 1000) branch (default 0)
     */
    static formatCompact = (
        num: any,
        opts: { space?: boolean; decimals?: number } = {}
    ): string => {
        const { space = true, decimals = 0 } = opts;
        const prefix = '₹' + (space ? ' ' : '');
        if (num == null || isNaN(num)) return prefix + '0';

        const abs = Math.abs(Number(num));
        const sign = Number(num) < 0 ? '-' : '';
        // Strip a trailing ".0" from the suffixed values, e.g. "1.0Cr" -> "1Cr".
        const strip = (s: string) => s.replace(/\.0$/, '');

        if (abs >= 10000000) return `${sign}${prefix}` + strip((abs / 10000000).toFixed(1)) + 'Cr';
        if (abs >= 100000)   return `${sign}${prefix}` + strip((abs / 100000).toFixed(1)) + 'L';
        if (abs >= 1000)     return `${sign}${prefix}` + strip((abs / 1000).toFixed(1)) + 'K';
        return `${sign}${prefix}` + Number(abs.toFixed(decimals)).toLocaleString('en-IN');
    }

    /**
     * Smart Indian currency — full grouped amount below 1 crore, Cr suffix above.
     * Rounds to whole rupees, so 184.51999999999998 -> "₹ 185". Use for KPI
     * tiles / card values where the exact grouped figure is wanted.
     */
    static formatSmart = (num: any, showCurrency: boolean = true): string => {
        const prefix = showCurrency ? '₹ ' : '';
        if (num == null || isNaN(num)) return prefix + '0';
        const abs = Math.abs(Number(num));
        const sign = Number(num) < 0 ? '-' : '';
        if (abs >= 10000000) {
            return `${sign}${prefix}` + (abs / 10000000).toFixed(2).replace(/\.?0+$/, '') + ' Cr';
        }
        return `${sign}${prefix}` + Math.round(abs).toLocaleString('en-IN');
    }

    static formatDate = (date: any) => {
        const today = new Date(date);
        const yyyy = today.getFullYear();
        let mm: any = today.getMonth() + 1; // Months start at 0!
        let dd : any = today.getDate();

        if (dd < 10) dd = '0' + dd;
        if (mm < 10) mm = '0' + mm;

        const formattedToday = dd + '/' + mm + '/' + yyyy;
        return formattedToday;
    }

    static getSubstringWithDots = (stringValue: String, charLimit: number) => {
        return String(stringValue).length > charLimit ? String(stringValue).substring(0,charLimit) + '...' : String(stringValue)
    }

    static getInvoiceRouteName = (voucherType: string) => {
        let invoiceMeta = Object();
        if (voucherType == InvoiceRoute.CREDIT_NOTE || voucherType === VocuherTypes.CREDIT_NOTE) {
            invoiceMeta.pageTitle =  VocuherTypes.CREDIT_NOTE
            invoiceMeta.voucherType =  VocuherTypes.CREDIT_NOTE
            invoiceMeta.routeName = InvoiceRoute.CREDIT_NOTE
        } else if (voucherType == InvoiceRoute.DEBIT_NOTE || voucherType === VocuherTypes.DEBIT_NOTE) {
            invoiceMeta.pageTitle =  VocuherTypes.DEBIT_NOTE
            invoiceMeta.voucherType =  VocuherTypes.DEBIT_NOTE
            invoiceMeta.routeName = InvoiceRoute.DEBIT_NOTE
        } else if (voucherType == InvoiceRoute.ESTIMATE || voucherType === VocuherTypes.ESTIMATE) {
            invoiceMeta.pageTitle =  VocuherTypes.ESTIMATE
            invoiceMeta.voucherType =  VocuherTypes.ESTIMATE
            invoiceMeta.routeName = InvoiceRoute.ESTIMATE
        } else if (voucherType == InvoiceRoute.DELIVERY_NOTE || voucherType === VocuherTypes.DELIVERY_NOTE) {
            invoiceMeta.pageTitle =  VocuherTypes.DELIVERY_NOTE
            invoiceMeta.voucherType =  VocuherTypes.DELIVERY_NOTE
            invoiceMeta.routeName = InvoiceRoute.DELIVERY_NOTE
        }else if (voucherType == InvoiceRoute.GST_INVOICE || voucherType === VocuherTypes.GST_INVOICE) {
            invoiceMeta.pageTitle =  VocuherTypes.GST_INVOICE
            invoiceMeta.voucherType =  VocuherTypes.GST_INVOICE
            invoiceMeta.routeName = InvoiceRoute.GST_INVOICE
        } else if (voucherType == InvoiceRoute.NON_GST_INVOICE || voucherType === VocuherTypes.NON_GST_INVOICE) {
            invoiceMeta.pageTitle =  VocuherTypes.NON_GST_INVOICE
            invoiceMeta.voucherType =  VocuherTypes.NON_GST_INVOICE
            invoiceMeta.routeName = InvoiceRoute.NON_GST_INVOICE
        } else if (voucherType == InvoiceRoute.PROFORMA_INVOICE || voucherType === VocuherTypes.PROFORMA_INVOICE) {
            invoiceMeta.pageTitle =  VocuherTypes.PROFORMA_INVOICE
            invoiceMeta.voucherType =  VocuherTypes.PROFORMA_INVOICE
            invoiceMeta.routeName = InvoiceRoute.PROFORMA_INVOICE
        } else if (voucherType == InvoiceRoute.PURCHASE_INVOICE || voucherType === VocuherTypes.PURCHASE_INVOICE) {
            invoiceMeta.pageTitle =  VocuherTypes.PURCHASE_INVOICE
            invoiceMeta.voucherType =  VocuherTypes.PURCHASE_INVOICE
            invoiceMeta.routeName = InvoiceRoute.PURCHASE_INVOICE
        }  else if (voucherType == InvoiceRoute.PURCHASE_ORDER || voucherType === VocuherTypes.PURCHASE_ORDER) {
            invoiceMeta.pageTitle =  VocuherTypes.PURCHASE_ORDER
            invoiceMeta.voucherType =  VocuherTypes.PURCHASE_ORDER
            invoiceMeta.routeName = InvoiceRoute.PURCHASE_ORDER
        } else {
            invoiceMeta.pageTitle =  ""
            invoiceMeta.voucherType =  ""
            invoiceMeta.routeName = "invoice"
            // toast.error("Could not found the Voucher Type " + voucherType);
        }

        return invoiceMeta
    }
    
    static toFixedWithoutZeros = (num: number, precision: number ) => {
        return `${Number.parseFloat(num.toFixed(precision))}`;
    }

    static capitalizeFirstLetter = (sentence: string) => {
        return sentence.replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());
    }

    static getFinancialYearDates = () => {
        // Calculates the current and last financial year start and end dates
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth(); // Month is 0-based in JavaScript (0 = January)
    
        let startYear, endYear;
    
        // Financial year starts in April
        if (currentMonth >= 3) {
            // If current month is April (3) or later
            startYear = currentYear;
            endYear = currentYear + 1;
        } else {
            // If current month is before April (Jan, Feb, Mar)
            startYear = currentYear - 1;
            endYear = currentYear;
        }
    
        const startDateFY = new Date(startYear, 3, 1); // April 1st
        const endDateFY = new Date(endYear, 2, 31);   // March 31st
    
        return { startDateFY, endDateFY };
    };

    static getLastFinancialYearDates = () => {
        // Calculates the current and last financial year start and end dates
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth(); // Month is 0-based in JavaScript (0 = January)
    
        let startYear, endYear;
    
        // Financial year starts in April
        if (currentMonth >= 3) {
            // If current month is April (3) or later
            startYear = currentYear - 1;
            endYear = currentYear;
        } else {
            // If current month is before April (Jan, Feb, Mar)
            startYear = currentYear - 2;
            endYear = currentYear - 1;
        }
    
        const startDateLastFY = new Date(startYear, 3, 1); // April 1st
        const endDateLastFY = new Date(endYear, 2, 31);   // March 31st
    
        return { startDateLastFY, endDateLastFY };
    };

    static getFinancialYearQuarterDates = () => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();
    
        let startYear, endYear;
    
        // Determine the financial year
        if (currentMonth >= 3) {
            // If current month is April (3) or later
            startYear = currentYear;
            endYear = currentYear + 1;
        } else {
            // If current month is before April (Jan, Feb, Mar)
            startYear = currentYear - 1;
            endYear = currentYear;
        }
    
        // Define quarter start and end months
        const quarters = [
            { start: new Date(startYear, 3, 1), end: new Date(startYear, 5, 30) }, // Q1: Apr-Jun
            { start: new Date(startYear, 6, 1), end: new Date(startYear, 8, 30) }, // Q2: Jul-Sep
            { start: new Date(startYear, 9, 1), end: new Date(startYear, 11, 31) }, // Q3: Oct-Dec
            { start: new Date(endYear, 0, 1), end: new Date(endYear, 2, 31) },     // Q4: Jan-Mar
        ];
    
        return quarters;
    };
    
    static additionalChargesSum = (invoice: any) => {
        let _additionalCharges;
        let initialSum = 0;
        if (invoice.additionalCharges && invoice.additionalCharges.charges && invoice.additionalCharges.charges.length > 0) {
          _additionalCharges = invoice.additionalCharges.charges.reduce((previousValue: any, currentValue: any) => previousValue + Number(currentValue.value), initialSum);
        } else {
          _additionalCharges = 0;
        }
        return _additionalCharges;
      };

}




export const enum InvoiceRoute {
    GST_INVOICE = "gst_invoice",
    NON_GST_INVOICE = "non_gst_invoice",
    CREDIT_NOTE = "credit_note",
    DEBIT_NOTE = "debit_note",
    ESTIMATE = "estimate",
    DELIVERY_NOTE = "delivery_note",
    PROFORMA_INVOICE = "proforma_invoice",
    PURCHASE_INVOICE = "purchase_invoice",
    PURCHASE_ORDER = "purchase_order"
}

export const enum VocuherTypes {
    GST_INVOICE = "Invoice",
    NON_GST_INVOICE = "Non GST Invoice",
    CREDIT_NOTE = "Credit Note",
    DEBIT_NOTE = "Debit Note",
    ESTIMATE = "Quotation",
    DELIVERY_NOTE = "Delivery Note",
    PROFORMA_INVOICE = "Proforma Invoice",
    PURCHASE_INVOICE = "Purchase Invoice",
    PURCHASE_ORDER = "Purchase Order",
}


export const enum VocuherCategory {
    SALE = "sales",
    PURCHASE = "purchase",
}

export const enum ReportTypes {
    GST_SALES = "GST_SALES",
    GST_PURCHASE = "GST_PURCHASE",
    TDS_PAYABLE = "TDS_PAYABLE",
    TDS_RECEIVEABLES = "TDS_RECEIVEABLES",
    TCS_PAYABLE = "TCS_PAYABLE",
    TCS_RECEIVEABLES = "TCS_RECEIVEABLES",
    PARTY_LEDGER = "PARTY_LEDGER",
    PARTY_LEDGER_REPORT = "PARTY_LEDGER_REPORT",
    PARTY_WISE_OUTSTANDING = "PARTY_WISE_OUTSTANDING",
}